import type React from 'react';
import { type ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomToast from 'components/CustomToast';
import { usePusher } from 'context/pusher';
import { useAppSelector } from 'store/customHooks';
import { type IObject } from 'types';

interface Props {
  children: ReactNode;
}

interface IIds {
  brandId: string;
  branchId: string;
  tableId: string;
}

interface IOrderDiscardData extends IObject {
  isDiscard: boolean;
  orderId: number;
  message: string;
}

const OrderDiscardContext = createContext<IOrderDiscardData | null>(null);

const OrderDiscardChannelProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pusher = usePusher();

  const { orderDetails } = useAppSelector((state) => state.tableStatusAndOrder);
  const orderId = sessionStorage.getItem('OrderId');

  const [discardData, setDiscardData] = useState<IOrderDiscardData | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');

  const ids = sessionStorage.getItem('Ids');
  const parsedIds: IIds = useMemo(() => {
    if (ids) {
      return JSON.parse(ids);
    }
  }, [ids]);

  useEffect(() => {
    if (pusher && parsedIds?.brandId && parsedIds?.branchId && parsedIds?.tableId) {
      const channelName = `tenant-${parsedIds.brandId}-branch-${parsedIds.branchId}-table-${parsedIds.tableId}-discardRefresh`;
      const channel = pusher.subscribe(channelName);

      channel.bind('ORDER_DISCARD_EVENT', (data: any) => {
        try {
          const parsedData = JSON.parse(data);
          const orderIdCheck = orderId || orderDetails?.id;

          if (
            parsedData &&
            parsedData.isDiscard &&
            String(parsedData.orderId) === String(orderIdCheck)
          ) {
            setDiscardData(parsedData);
          }
        } catch (error: any) {
          setToastMessage(error?.message || 'Something went wrong. please try again');
        }
      });

      channel.bind('pusher:subscription_error', (status: any) => {
        setToastMessage(status?.message || 'Failed to connect to real-time updates');
      });

      return () => {
        channel.unbind_all();
        pusher.unsubscribe(channelName);
      };
    }
  }, [pusher, parsedIds, orderId, orderDetails?.id]);

  useEffect(() => {
    if (discardData?.isDiscard) {
      // Redirect to session expired or a similar block page
      navigate('/session-expired', {
        state: {
          message: discardData.message || 'Session ended. Please scan the table again',
        },
      });
    }
  }, [discardData, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDiscardData(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [location]);

  return (
    <OrderDiscardContext.Provider value={discardData}>
      {toastMessage && (
        <CustomToast
          message={toastMessage}
          type='error'
          handleOnToastDismiss={() => setToastMessage('')}
        />
      )}
      {children}
    </OrderDiscardContext.Provider>
  );
};

const useOrderDiscardPusherChannel = (): IOrderDiscardData | null => {
  const context = useContext(OrderDiscardContext);
  return context;
};

export default OrderDiscardChannelProvider;
export { useOrderDiscardPusherChannel };
